/* eslint-disable complexity */
import { useEffect, Dispatch, SetStateAction, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import isEqual from 'lodash/isEqual'
import Cookies from 'js-cookie'
import { useUserRQInfo } from '../user-provider/user-provider'
import Link from '../Link'
import { StencilCDNImage } from '../StencilImage'
import { GS_SEARCH_FILTER_QUERY_PARAM_NAME } from '../global-search-v2/use-global-search-query-params'
import { TDesktopDropDownOverlay } from '../HeaderV2/types/types'
import DesktopStocksNavigationV2 from '../HeaderV2/DesktopStocksNavigationV2'
import MobileNavSlider from '../HeaderV2/MobileNavSlider'
import DesktopHeaderNavigation from './desktop-header-navigation'
import Logo from './logo'
import Hamburger from './hamburger'
import SearchIcon from './search-icon'
import SliderNav from './slide-nav'
import type { MobileOverlayStateType } from './utils/types'
import { HeaderRight } from './header-right'
import { eventData, eventMapping } from './header.tracking'
import HelpIcon from '@/stories/assets/header/help.svg'
// import GlobalSearchV2 from '../global-search-v2/search'
import useLockHTMLScroll from '@/hooks/useLockHTMLScroll'
import useMediaQuery from '@/hooks/useMediaQuery'
import { BETA_INDSTOCKS_HOST, INDSTOCKS_HOST } from '@/root/constants/common'
import RightArrow from '@/assets/common/arrow-right-inline.svg'
import { useAnalyticsEvent } from '@/hooks/analytics/use-analytics-event'
import { DASHBOARD_HEADER_NAV_CLICK } from '@/root/utils/common/web/gtm-events/eventsList'
import { CLOSE_SYMBOL } from '@/root/constants'
import PrettyBlueButton from '@/components/Stocks/TransactionSystem/PrettyBlueButton'

const IndicesNav = dynamic(
  () => import('@/components/TVChartContainer/IndicesNav'),
  {
    ssr: false,
  },
)

// const BellSvgIcon = dynamic(() => import('@/assets/header/bell.svg'), {
//   ssr: false,
// })

const MoreIcon = dynamic(() => import('./more-icon'), {
  ssr: false,
})

const GlobalSearchV2 = dynamic(
  () => import('../global-search-v2/global-search-v2'),
  {
    ssr: false,
  },
)

const DesktopNavigationV2 = dynamic(
  () => import('../HeaderV2/DesktopNavigationV2'),
  {
    ssr: true,
  },
)
const DropdownOverlay = dynamic(() => import('../HeaderV2/DropdownOverlay'), {
  ssr: true,
})

// Add pathnames here where you want the POD navigation chips to be removed and the search bar width to be increased
export const disableNavigationChipsOnPathnamesArr = [
  '/stocks',
  '/investments/stocks/my-ind-stocks',
  '/investments/stocks/transactions',
  '/investments/stocks/wallet-history',
  '/investments/stocks/wallet-history/all',
  '/investments/stocks/wallet-history/transaction',
  '/investments/stocks/watchlists',
  '/futures-and-options',
  '/investments/futures-and-options/orders',
  '/futures-and-options/options-chain/[slug]',
  '/investments/futures-and-options/my-positions',
  '/investments/mutual-fund/my-funds',
  '/investments/mutual-fund/performance-summary',
  '/investments/mutual-fund/explore-all',
  '/investments/mutual-fund/transactions',
  '/investments/fixed-deposit/explore-all',
  '/investments/fixed-deposit/my-fixed-deposits',
  '/investments/us-stocks/explore-all',
  '/investments/us-stocks/todays-trades',
  '/investments/us-stocks/my-us-stocks',
  '/investments/us-stocks/wallet-history',
  '/stocks/ipo',
]

// eslint-disable-next-line max-lines-per-function
export function Header({
  isLargerThan1025,
  overlay,
  setOverlay,
  host,
  isInMobile,
}: {
  isLargerThan1025: boolean | null
  overlay: MobileOverlayStateType
  setOverlay: Dispatch<SetStateAction<MobileOverlayStateType>>
  host: string
  isInMobile?: boolean
}) {
  const router = useRouter()
  const { query, pathname } = router
  const pushEvent = useAnalyticsEvent()

  const link =
    host === INDSTOCKS_HOST
      ? '/customer-service?topic_id=9&searchFilter=FAQ'
      : '/customer-service'

  const {
    userBasicRQData,
    userProfileRQData,
    isAuthenticated,
    isNotAuthenticated,
  } = useUserRQInfo()

  const memberId = query.memberId ?? Cookies.get('memberId')
  const firstName = memberId
      ? userProfileRQData.data?.familyMembers?.find(
          item => item?.id?.toString() == memberId,
        )?.name
      : userBasicRQData.data?.first_name,
    lastName = memberId ? '' : userBasicRQData.data?.last_name

  const initOverlayValue: MobileOverlayStateType =
    isLargerThan1025 === false &&
    router.query[GS_SEARCH_FILTER_QUERY_PARAM_NAME]
      ? 'search-overlay'
      : null

  const [dwebDropdown, toggleDwebDropdown] =
    useState<TDesktopDropDownOverlay>(null)
  const [mwebNavigation, toggleMwebNavigation] = useState<boolean>(false)

  useEffect(() => {
    if (memberId) {
      setOverlay(null)
    }
    if (initOverlayValue === 'search-overlay') {
      setOverlay(initOverlayValue)
    }
  }, [initOverlayValue, memberId, setOverlay, router.query])

  const toggleOverlay = (toggleOn: Exclude<MobileOverlayStateType, null>) => {
    return () => {
      setOverlay(prevState => {
        if (isEqual(prevState, toggleOn)) {
          return null
        } else if (
          typeof prevState === 'object' &&
          prevState?.type === 'logged-in-watchlist-overlay'
        ) {
          return null
        } else {
          return toggleOn
        }
      })
    }
  }

  const toggleSearchOverlay = toggleOverlay('search-overlay')
  const toggleMoreOverlay = toggleOverlay('more-overlay')
  // const toggleNavOverlay = toggleOverlay('nav-overlay')
  const toggleLoggedInSidebarOverlay = toggleOverlay(
    'logged-in-sidebar-overlay',
  )
  const toggleWatchlistOverlay = toggleOverlay({
    type: 'logged-in-watchlist-overlay',
    params: {
      mode: 'dashboard',
    },
  })

  const handleNavToggleWithEvents = e => {
    e.stopPropagation()
    let gtm4Payload
    if (mwebNavigation) {
      gtm4Payload = {
        eventData: eventMapping[eventData.headerCrossButtonClick],
      }
    } else {
      gtm4Payload = {
        eventData: eventMapping[eventData.headerHamburgerClick],
      }
    }
    pushEvent({ gtm4Payload })
    toggleMwebNavigation(!mwebNavigation)
  }

  useLockHTMLScroll({
    stateOnWhichLockToggles: overlay,
  })

  const disableNavigationChips =
    disableNavigationChipsOnPathnamesArr.includes(pathname) && isAuthenticated
  const isStocksFunnel = host === INDSTOCKS_HOST || host === BETA_INDSTOCKS_HOST
  return (
    <header className="sticky left-0 top-0 z-50 h-[85px]">
      <div
        className={classNames(
          'bg-brand-white border-b-1 h-full w-full border-[#1919190d]',
          {
            '!border-b-0':
              disableNavigationChips ||
              (isStocksFunnel && pathname === '/dashboard'),
          },
        )}
        id="site-header"
      >
        <div
          className={classNames(
            'herolike-wrapper flex items-center justify-end py-4 lg:justify-center',
            { '!w-full px-2': pathname.includes('/technicalchart') },
          )}
        >
          <section className="flex flex-1 items-center">
            <aside className="flex flex-1 items-center pr-3">
              <div className="mr-4">
                <Logo
                  host={host}
                  toggleDwebDropdown={toggleDwebDropdown}
                  setOverlay={setOverlay}
                />
              </div>
              <section className={classNames('hidden lg:block')}>
                {isLargerThan1025 && (
                  <div className="hidden lg:block">
                    <GlobalSearchV2
                      host={host}
                      toggleDwebDropdown={toggleDwebDropdown}
                    />
                  </div>
                )}
              </section>
            </aside>
          </section>

          {pathname.includes('/technicalchart') && <IndicesNav />}

          <section className={classNames({ hidden: !isInMobile })}>
            {/* SEARCH */}
            <aside className="text-brand-green flex items-center space-x-3">
              {isNotAuthenticated && (
                <>
                  <div
                    onClick={() => {
                      toggleMwebNavigation(false)
                      void router.push(link)
                    }}
                    title="Log In to INDmoney"
                  >
                    <PrettyBlueButton
                      borderColorPrimary="border-brand-green-dark"
                      backgroundColorSecondary="bg-brand-white"
                      textColorPrimary="text-brand-green-dark"
                      paddingClassNames="px-2.5 py-1"
                      primary={false}
                      hover={false}
                      text="Log In"
                    />
                  </div>
                  <div
                    title="Help Center"
                    className="cursor-pointer"
                    onClick={() => {
                      pushEvent({
                        gtm4Payload: {
                          eventData:
                            eventMapping[eventData.headerHelpCenterIconClick],
                        },
                      })
                      setOverlay(null)
                      toggleMwebNavigation(false)
                      void router.push(link)
                    }}
                  >
                    <HelpIcon className="aspect-square w-6" />
                  </div>
                </>
              )}
              <div
                onClick={() => {
                  pushEvent({
                    gtm4Payload: {
                      eventData: eventMapping[eventData.headerSearchClick],
                    },
                  })
                  toggleSearchOverlay()
                  toggleMwebNavigation(false)
                }}
              >
                <SearchIcon />
              </div>
              {isAuthenticated && (
                <div onClick={toggleMoreOverlay}>
                  <MoreIcon />
                </div>
              )}

              {isNotAuthenticated && (
                <div onClick={handleNavToggleWithEvents}>
                  {mwebNavigation ? (
                    <div className="text-24px text-brand-grey-base text-right">
                      {/* cross */}
                      {CLOSE_SYMBOL}
                    </div>
                  ) : (
                    <Hamburger />
                  )}
                </div>
              )}
            </aside>
          </section>
          {/* show hamburger on tab screens: logged out */}
          {/* {!isAuthenticated && (
          <section onClick={toggleNavOverlay} className="block lg:hidden">
            <Hamburger />
          </section>
        )} */}

          {/* full length navigation for bigger screens */}

          {!isInMobile && isNotAuthenticated && isStocksFunnel && (
            <DesktopStocksNavigationV2 host={host} />
          )}

          {!isInMobile && isAuthenticated && (
            <div
              className={classNames('hidden xl:block', {
                'lg:!hidden xl:!hidden':
                  disableNavigationChips ||
                  (isStocksFunnel && pathname === '/dashboard') ||
                  pathname.includes('/technicalchart'),
              })}
            >
              <DesktopHeaderNavigation host={host} />
            </div>
          )}

          {isNotAuthenticated &&
            !isInMobile &&
            !pathname.includes('/technicalchart') &&
            !isStocksFunnel && (
              <div className={classNames('md:block')}>
                <DesktopNavigationV2
                  dwebDropdown={dwebDropdown}
                  toggleDwebDropdown={toggleDwebDropdown}
                />
              </div>
            )}

          {isAuthenticated && (
            <div
              onClick={() => {
                pushEvent({
                  mixpanelEventPayload: {
                    eventName: DASHBOARD_HEADER_NAV_CLICK,
                    payload: {
                      cta_name: 'Watchlist',
                    },
                  },
                })
                toggleWatchlistOverlay()
              }}
              className="ml-3"
            >
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/watchlist_icon_top_nav.png"
                width={25}
                height={25}
                className="cursor-pointer"
              />
            </div>
          )}
          {/* Nishkarsh Tripathi has asked to remove */}
          {/* {isAuthenticated && !isStocksFunnel && (
          <Link
            href="/advisory-cards"
            nativeAnchor
            className="hover:text-brand-blue-azureRadiance ml-3 h-5 w-5"
          >
            <BellSvgIcon />
          </Link>
        )} */}

          {isAuthenticated && (
            <Link
              href="/profile/profile-details"
              nativeAnchor={true}
              className="bg-brand-tertiary-blue hover:text-brand-blue-azureRadiance ml-3 flex h-8 max-h-8 min-h-8 w-8 items-center justify-center rounded-full text-sm"
            >
              {firstName && <span>{firstName[0].toUpperCase()}</span>}
              {lastName && <span>{lastName[0].toUpperCase()}</span>}
              {!firstName && !lastName && <span>U</span>}
            </Link>
          )}

          {isStocksFunnel && (
            <Link
              href="https://www.indmoney.com"
              className={classNames(
                'text-brand-blue-dark bg-brand-grey-background hidden items-center justify-center !rounded-[70px] px-5 py-[10px] font-medium lg:flex',
                'ml-2 cursor-pointer rounded-xl',
              )}
              target="_blank"
              title="Log In to INDmoney"
            >
              <p>INDmoney.com</p>
              <RightArrow
                width="20"
                height="20"
                className="text-brand-black mt-1 flex-shrink-0"
              />
            </Link>
          )}
          {isNotAuthenticated && !isInMobile && (
            <HeaderRight host={host} toggleDwebDropdown={toggleDwebDropdown} />
          )}

          {/* show logged in sidebar hamburger toggler */}
          {isAuthenticated && (
            <div
              onClick={() => {
                pushEvent({
                  mixpanelEventPayload: {
                    eventName: DASHBOARD_HEADER_NAV_CLICK,
                    payload: {
                      cta_name: 'Sidebar',
                    },
                  },
                })
                toggleLoggedInSidebarOverlay()
              }}
              className="ml-3"
            >
              <Hamburger />
            </div>
          )}
          <SliderNav
            overlay={overlay}
            isAuthenticated={isAuthenticated}
            toggleLoggedInSidebarOverlay={toggleLoggedInSidebarOverlay}
            toggleSearchOverlay={toggleSearchOverlay}
            toggleMoreOverlay={toggleMoreOverlay}
            // toggleNavOverlay={toggleNavOverlay}
            toggleWatchlistOverlay={toggleWatchlistOverlay}
            host={host}
            // TODO: rename this prop before merging
            data={{
              firstName,
              lastName,
              isSubscribed: !!userBasicRQData.data?.is_subscribed,
              subscription_plan: userBasicRQData.data?.subscription_plan,
              profileData: userProfileRQData,
            }}
            // isInMobile={isInMobile}
            // setOverlay={setOverlay}
          />
          {isInMobile && (
            <MobileNavSlider
              host={host}
              isAuthenticated={isAuthenticated}
              mwebNavigation={mwebNavigation}
              toggleMwebNavigation={toggleMwebNavigation}
            />
          )}
        </div>
      </div>
      {!isInMobile && isNotAuthenticated && !isStocksFunnel && (
        <DropdownOverlay
          dwebDropdown={dwebDropdown}
          toggleDwebDropdown={toggleDwebDropdown}
        />
      )}
    </header>
  )
}

export default function HeaderWithMediaQuery({
  overlay,
  setOverlay,
  host,
  isInMobile,
}: {
  overlay: MobileOverlayStateType
  setOverlay: Dispatch<SetStateAction<MobileOverlayStateType>>
  host: string
  isInMobile?: boolean
}) {
  const isLargerThan1025 = useMediaQuery({ breakpointToken: 'lg' })

  return (
    <Header
      isLargerThan1025={isLargerThan1025}
      overlay={overlay}
      setOverlay={setOverlay}
      key={isLargerThan1025?.toString()}
      host={host}
      isInMobile={isInMobile}
    />
  )
}
